<template>
    <b-modal v-model="visibleModel" title="시설 담당자 관리" size="md" centered @show="onShow">
        <template #modal-header="{ close }">
            <div class="modal-detail-header">
                <h5 class="modal-title">
                    시설 담당자 — {{ facility ? `${facility.facilityCode} ${facility.facilityName || ""}` : "" }}
                </h5>
                <button type="button" class="close" @click="close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
        </template>

        <div v-if="facility" class="assignees-content">
            <div v-if="loading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</div>
            <template v-else>
                <!-- 일반 모드: 목록 + 1명 추가/해제 -->
                <template v-if="!replaceMode">
                    <div class="table-toolbar">
                        <span class="table-toolbar-label">담당자 목록</span>
                        <button
                            type="button"
                            class="btn-link-replace"
                            title="목록을 일괄 수정합니다"
                            @click="enterReplaceMode"
                        >
                            <i class="fas fa-exchange-alt"></i> 일괄 교체
                        </button>
                    </div>
                    <div class="table-container-compact">
                        <table class="table-compact">
                            <thead>
                                <tr>
                                    <th class="text-center">사용자 ID</th>
                                    <th class="text-center">이름</th>
                                    <th class="text-center">접근 레벨</th>
                                    <th class="text-center">비고</th>
                                    <th class="text-center col-action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="assignees.length === 0">
                                    <td colspan="5" class="text-center empty-state">
                                        <i class="fas fa-users"></i>
                                        <p>담당자가 없습니다.</p>
                                    </td>
                                </tr>
                                <tr v-for="a in assignees" :key="a.userFacilityIdx || a.userIdx">
                                    <td class="text-center">
                                        <strong class="facility-code-cell">{{ a.loginId }}</strong>
                                    </td>
                                    <td class="text-center">{{ a.userName || "-" }}</td>
                                    <td class="text-center">{{ formatAccessLevel(a.accessLevel) }}</td>
                                    <td class="text-center">{{ a.remarks || "-" }}</td>
                                    <td class="text-center col-action">
                                        <div class="action-buttons">
                                            <IconButton
                                                icon="fas fa-times"
                                                size="small"
                                                variant="delete"
                                                title="담당자 해제"
                                                @click="removeAssignee(a)"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="form-section add-section">
                        <div class="section-header">
                            <h6>담당자 추가</h6>
                        </div>
                        <div class="add-form-row">
                            <div class="form-group">
                                <label>Carbon 사용자</label>
                                <CarbonSelect
                                    v-model="addForm.userIdx"
                                    :options="userOptionsForSelect"
                                    placeholder="선택하세요"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group">
                                <label>접근 레벨</label>
                                <CarbonSelect
                                    v-model="addForm.accessLevel"
                                    :options="accessLevelOptions"
                                    placeholder="선택하세요"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group">
                                <label>비고</label>
                                <CarbonInput
                                    v-model="addForm.remarks"
                                    placeholder="선택"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group form-group-btn">
                                <CarbonButton
                                    variant="primary"
                                    icon="fas fa-plus"
                                    :loading="adding"
                                    :disabled="adding || !addForm.userIdx || !addForm.accessLevel"
                                    @click="addAssignee"
                                >
                                    추가
                                </CarbonButton>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- 일괄 교체 모드: 편집 가능 목록 + 행 추가 -->
                <template v-else>
                    <div class="replace-mode-notice">
                        <i class="fas fa-info-circle"></i>
                        시설 담당자를 추가 또는 수정한 뒤 "일괄 적용"으로 저장하세요. 취소하면 변경 사항이 반영되지
                        않습니다.
                    </div>
                    <div class="table-container-compact">
                        <table class="table-compact">
                            <thead>
                                <tr>
                                    <th class="text-center">사용자 ID</th>
                                    <th class="text-center">이름</th>
                                    <th class="text-center">접근 레벨</th>
                                    <th class="text-center">비고</th>
                                    <th class="text-center col-action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="replaceDraft.length === 0">
                                    <td colspan="5" class="text-center empty-state">
                                        <i class="fas fa-users"></i>
                                        <p>담당자가 없습니다. 아래에서 행을 추가하세요.</p>
                                    </td>
                                </tr>
                                <tr v-for="row in replaceDraft" :key="row.userIdx">
                                    <td class="text-center">
                                        <strong class="facility-code-cell">{{ row.loginId }}</strong>
                                    </td>
                                    <td class="text-center">{{ row.userName || "-" }}</td>
                                    <td class="text-center">
                                        <CarbonSelect
                                            v-model="row.accessLevel"
                                            :options="accessLevelOptions"
                                            custom-class="form-control-inline"
                                        />
                                    </td>
                                    <td class="text-center">
                                        <CarbonInput
                                            v-model="row.remarks"
                                            placeholder="선택"
                                            custom-class="form-control-inline"
                                        />
                                    </td>
                                    <td class="text-center col-action">
                                        <IconButton
                                            icon="fas fa-times"
                                            size="small"
                                            variant="delete"
                                            title="행 삭제"
                                            @click="removeReplaceDraftRow(row)"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="form-section add-section">
                        <div class="section-header">
                            <h6>행 추가</h6>
                        </div>
                        <div class="add-form-row">
                            <div class="form-group">
                                <label>Carbon 사용자</label>
                                <CarbonSelect
                                    v-model="replaceAddForm.userIdx"
                                    :options="replaceAvailableUserOptions"
                                    placeholder="선택하세요"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group">
                                <label>접근 레벨</label>
                                <CarbonSelect
                                    v-model="replaceAddForm.accessLevel"
                                    :options="accessLevelOptions"
                                    placeholder="선택하세요"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group">
                                <label>비고</label>
                                <CarbonInput
                                    v-model="replaceAddForm.remarks"
                                    placeholder="선택"
                                    custom-class="form-control-compact"
                                />
                            </div>
                            <div class="form-group form-group-btn">
                                <CarbonButton
                                    variant="primary"
                                    icon="fas fa-plus"
                                    :disabled="!replaceAddForm.userIdx || !replaceAddForm.accessLevel"
                                    @click="addReplaceDraftRow"
                                >
                                    추가
                                </CarbonButton>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </div>

        <template #modal-footer>
            <div class="modal-footer-actions">
                <template v-if="!replaceMode">
                    <CarbonButton variant="secondary" @click="visibleModel = false">닫기</CarbonButton>
                </template>
                <template v-else>
                    <CarbonButton variant="secondary" @click="exitReplaceMode">취소</CarbonButton>
                    <CarbonButton variant="primary" :loading="replacing" :disabled="replacing" @click="applyReplace">
                        일괄 적용
                    </CarbonButton>
                </template>
            </div>
        </template>
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";
import CarbonCodes from "../../../codes";
import CarbonSelect from "../../common/CarbonSelect.vue";
import CarbonInput from "../../common/CarbonInput.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import IconButton from "../../common/IconButton.vue";

export default {
    name: "FacilityAssigneesModal",
    components: {
        CarbonSelect,
        CarbonInput,
        CarbonButton,
        IconButton,
    },
    props: {
        value: { type: Boolean, default: false },
        facility: { type: Object, default: null },
    },
    data() {
        return {
            assignees: [],
            loading: false,
            adding: false,
            carbonUsers: [],
            addForm: {
                userIdx: null,
                accessLevel: "READ",
                remarks: "",
            },
            // 일괄 교체 모드
            replaceMode: false,
            replaceDraft: [],
            replaceAddForm: {
                userIdx: null,
                accessLevel: "READ",
                remarks: "",
            },
            replacing: false,
        };
    },
    computed: {
        visibleModel: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        assignedUserIds() {
            return new Set(this.assignees.map((a) => a.userIdx).filter(Boolean));
        },
        availableUsers() {
            return this.carbonUsers.filter((u) => !this.assignedUserIds.has(u.userIdx));
        },
        userOptionsForSelect() {
            return this.availableUsers.map((u) => ({
                value: u.userIdx,
                text: `${u.loginId} — ${u.userName || "-"}`,
            }));
        },
        accessLevelOptions() {
            return CarbonCodes.accessLevel
                ? CarbonCodes.accessLevel.getList()
                : [
                      { value: "READ", text: "읽기" },
                      { value: "WRITE", text: "쓰기" },
                      { value: "FULL", text: "전체" },
                  ];
        },
        replaceAvailableUserOptions() {
            const draftUserIds = new Set(this.replaceDraft.map((d) => d.userIdx).filter((id) => id != null));
            return this.carbonUsers
                .filter((u) => !draftUserIds.has(u.userIdx))
                .map((u) => ({
                    value: u.userIdx,
                    text: `${u.loginId} — ${u.userName || "-"}`,
                }));
        },
    },
    methods: {
        formatAccessLevel(level) {
            return CarbonCodes.accessLevel ? CarbonCodes.accessLevel.getLabel(level) : level || "-";
        },
        async onShow() {
            if (this.facility?.facilityIdx) {
                await this.loadAssignees();
                await this.loadCarbonUsers();
            }
            this.addForm = { userIdx: null, accessLevel: "READ", remarks: "" };
            this.replaceMode = false;
            this.replaceDraft = [];
            this.replaceAddForm = { userIdx: null, accessLevel: "READ", remarks: "" };
        },
        async loadAssignees() {
            if (!this.facility?.facilityIdx) return;
            this.loading = true;
            try {
                const res = await carbonApi.utility.getFacilityAssignees(this.facility.facilityIdx);
                const data = res.data?.data != null ? res.data.data : res.data;
                this.assignees = (data && data.assignees) || [];
            } catch (err) {
                console.error("[FacilityAssigneesModal] 담당자 목록 조회 실패:", err);
                this.$bvToast?.toast(err.response?.data?.message || "담당자 목록을 불러오지 못했습니다.", {
                    title: "오류",
                    variant: "danger",
                });
                this.assignees = [];
            } finally {
                this.loading = false;
            }
        },
        async loadCarbonUsers() {
            try {
                const { data } = await carbonApi.accessControl.getUsers({});
                if (data?.success && Array.isArray(data.data?.users)) {
                    this.carbonUsers = (data.data.users || []).map((u) => ({
                        userIdx: u.userIdx,
                        loginId: u.loginId,
                        userName: u.userName,
                    }));
                } else {
                    this.carbonUsers = [];
                }
            } catch (err) {
                console.error("[FacilityAssigneesModal] 사용자 목록 조회 실패:", err);
                this.carbonUsers = [];
            }
        },
        async addAssignee() {
            const userIdx = this.addForm.userIdx;
            if (!this.facility?.facilityIdx || (userIdx !== 0 && !userIdx) || !this.addForm.accessLevel) return;
            this.adding = true;
            try {
                await carbonApi.utility.addFacilityAssignee(this.facility.facilityIdx, {
                    userIdx: Number(this.addForm.userIdx),
                    accessLevel: this.addForm.accessLevel,
                    remarks: this.addForm.remarks?.trim() || null,
                });
                this.$bvToast?.toast("담당자가 추가되었습니다.", { variant: "success" });
                this.addForm = { userIdx: null, accessLevel: "READ", remarks: "" };
                await this.loadAssignees();
                this.$emit("updated");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "담당자 추가에 실패했습니다.";
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger" });
            } finally {
                this.adding = false;
            }
        },
        async removeAssignee(row) {
            if (!this.facility?.facilityIdx || row.userIdx == null) return;
            const name = row.userName || row.loginId || "해당 사용자";
            const ok = await this.$bvModal.msgBoxConfirm(`"${name}" 담당자 권한을 해제하시겠습니까?`, {
                title: "담당자 해제",
                okTitle: "해제",
                cancelTitle: "취소",
                okVariant: "danger",
            });
            if (!ok) return;
            try {
                await carbonApi.utility.removeFacilityAssignee(this.facility.facilityIdx, row.userIdx);
                this.$bvToast?.toast("담당자가 해제되었습니다.", { variant: "success" });
                await this.loadAssignees();
                this.$emit("updated");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "담당자 해제에 실패했습니다.";
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger" });
            }
        },

        enterReplaceMode() {
            this.replaceDraft = this.assignees.map((a) => ({
                userIdx: a.userIdx,
                loginId: a.loginId,
                userName: a.userName,
                accessLevel: a.accessLevel || "READ",
                remarks: a.remarks || "",
            }));
            this.replaceAddForm = { userIdx: null, accessLevel: "READ", remarks: "" };
            this.replaceMode = true;
        },

        exitReplaceMode() {
            this.replaceMode = false;
            this.replaceDraft = [];
            this.replaceAddForm = { userIdx: null, accessLevel: "READ", remarks: "" };
        },

        addReplaceDraftRow() {
            const userIdx = this.replaceAddForm.userIdx;
            if (userIdx === null || userIdx === "" || !this.replaceAddForm.accessLevel) return;
            const user = this.carbonUsers.find((u) => u.userIdx === userIdx);
            if (!user) return;
            const already = this.replaceDraft.some((d) => d.userIdx === userIdx);
            if (already) {
                this.$bvToast?.toast("이미 목록에 있는 사용자입니다.", { variant: "warning" });
                return;
            }
            this.replaceDraft.push({
                userIdx: user.userIdx,
                loginId: user.loginId,
                userName: user.userName,
                accessLevel: this.replaceAddForm.accessLevel,
                remarks: this.replaceAddForm.remarks || "",
            });
            this.replaceAddForm = { userIdx: null, accessLevel: "READ", remarks: "" };
        },

        removeReplaceDraftRow(row) {
            this.replaceDraft = this.replaceDraft.filter((d) => d.userIdx !== row.userIdx);
        },

        async applyReplace() {
            if (!this.facility?.facilityIdx) return;
            const assignees = this.replaceDraft.map((d) => ({
                userIdx: d.userIdx,
                accessLevel: d.accessLevel || "READ",
                remarks: d.remarks?.trim() || null,
            }));
            if (assignees.length === 0) {
                const ok = await this.$bvModal.msgBoxConfirm("담당자를 모두 제거합니다. 계속하시겠습니까?", {
                    title: "일괄 적용",
                    okTitle: "적용",
                    cancelTitle: "취소",
                    okVariant: "warning",
                });
                if (!ok) return;
            }
            this.replacing = true;
            try {
                await carbonApi.utility.replaceFacilityAssignees(this.facility.facilityIdx, { assignees });
                this.$bvToast?.toast("담당자가 일괄 적용되었습니다.", { variant: "success" });
                this.exitReplaceMode();
                await this.loadAssignees();
                this.$emit("updated");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "일괄 적용에 실패했습니다.";
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger" });
            } finally {
                this.replacing = false;
            }
        },
    },
};
</script>

<style scoped>
.modal-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}
.modal-detail-header .modal-title {
    margin: 0;
    font-size: 1rem;
}
.assignees-content {
    min-height: 120px;
}

/* 테이블 위 툴바: 일괄 교체 링크 (방안 A) */
.table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}
.table-toolbar-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}
.btn-link-replace {
    padding: 4px 8px;
    font-size: 12px;
    color: #0ea5e9;
    background: none;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.btn-link-replace:hover {
    color: #0284c7;
    text-decoration: underline;
}
.btn-link-replace i {
    font-size: 11px;
}

/* 테이블: UserFacilityPermissionModal과 동일 */
.facility-code-cell {
    font-weight: 600;
}
.col-action {
    width: 80px;
}
/* action-buttons / empty-state / table-compact 룩은 전역 _carbon-tables.scss 적용 */

/* 폼 섹션: UserFacilityPermissionModal과 동일 */
.form-section {
    margin-bottom: 16px;
}
.add-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}
.section-header {
    margin-bottom: 8px;
}
.section-header h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}
.add-form-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    gap: 12px 16px;
}
.add-form-row .form-group {
    flex: 1;
    min-width: 0;
}
.add-form-row .form-group-btn {
    flex: 0 0 auto;
}
.form-group {
    margin-bottom: 12px;
}
.form-group-btn {
    margin-bottom: 12px;
}
.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 500;
}
.form-control-compact {
    width: 100%;
    min-width: 0;
}

/* 일괄 교체 모드 */
.replace-mode-notice {
    padding: 8px 12px;
    margin-bottom: 12px;
    font-size: 12px;
    color: #475569;
    background: #f1f5f9;
    border-radius: 6px;
    border-left: 3px solid #0ea5e9;
}
.replace-mode-notice i {
    margin-right: 6px;
    color: #0ea5e9;
}
.form-control-inline {
    width: 100%;
    min-width: 0;
    max-width: 140px;
    display: inline-block;
}
.modal-footer-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
}
</style>
